// src/components/academic/AcademicTerms.js
import React, { useState, useEffect } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Checkbox, Chip,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, IconButton, Paper, Snackbar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { academicAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const INITIAL = { name: '', start_date: '', end_date: '', year: new Date().getFullYear(), is_current: false };

const AcademicTerms = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.ACADEMIC.TERMS, ACTIONS.READ);
  const canCreate = can(RESOURCES.ACADEMIC.TERMS, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.ACADEMIC.TERMS, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.ACADEMIC.TERMS, ACTIONS.DELETE);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [terms, setTerms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [formData, setFormData] = useState(INITIAL);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { if (canRead) fetchTerms(); }, [canRead]);

  const fetchTerms = async () => {
    try {
      const response = await academicAPI.getTerms();
      setTerms(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch terms', severity: 'error' });
    }
  };

  const handleEdit = (term) => {
    setEditingTerm(term);
    setFormData({
      name: term.name,
      start_date: term.start_date?.split('T')[0] || term.start_date,
      end_date: term.end_date?.split('T')[0] || term.end_date,
      year: term.year,
      is_current: term.is_current,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTerm) {
        await academicAPI.updateTerm(editingTerm.id, formData);
        setSnackbar({ open: true, message: 'Term updated successfully', severity: 'success' });
      } else {
        await academicAPI.createTerm(formData);
        setSnackbar({ open: true, message: 'Term created successfully', severity: 'success' });
      }
      resetForm();
      fetchTerms();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Operation failed', severity: 'error' });
    }
  };

  const confirmDeleteAction = async () => {
    try {
      await academicAPI.deleteTerm(confirmDelete);
      setSnackbar({ open: true, message: 'Term deleted successfully', severity: 'success' });
      fetchTerms();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to delete term', severity: 'error' });
    } finally {
      setConfirmDelete(null);
    }
  };

  const setCurrentTerm = async (termId) => {
    try {
      await academicAPI.setCurrentTerm(termId);
      fetchTerms();
    } catch {
      setSnackbar({ open: true, message: 'Failed to set current term', severity: 'error' });
    }
  };

  const resetForm = () => { setFormData(INITIAL); setEditingTerm(null); setShowModal(false); };

  return (
    <Box>
      {!canRead && <Alert severity="error" sx={{ mb: 2 }}>You do not have permission to view terms.</Alert>}
      <ModulePageHeader
        title="Terms Management"
        subtitle="Create and manage academic terms"
        actions={canCreate ? [
          <Button key="add-term" variant="contained" startIcon={<AddIcon />} onClick={() => setShowModal(true)} fullWidth={isMobile}>
            Add Term
          </Button>
        ] : []}
      />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Academic Terms</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Term Name</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Duration (Days)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {terms.map(term => (
                  <TableRow key={term.id}>
                    <TableCell>{term.name}</TableCell>
                    <TableCell>{term.year}</TableCell>
                    <TableCell>{new Date(term.start_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(term.end_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {Math.ceil((new Date(term.end_date) - new Date(term.start_date)) / (1000 * 60 * 60 * 24))}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={term.is_current ? 'Current' : 'Inactive'}
                        color={term.is_current ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} alignItems="center">
                        {!term.is_current && canUpdate && (
                          <Button size="small" variant="outlined" onClick={() => setCurrentTerm(term.id)}>
                            Set Current
                          </Button>
                        )}
                        {canUpdate && (
                          <IconButton size="small" color="primary" onClick={() => handleEdit(term)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(term.id)} disabled={term.is_current}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTerm ? 'Edit Academic Term' : 'Add Academic Term'}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent>
            <Autocomplete
              fullWidth
              options={['Term 1', 'Term 2', 'Term 3']}
              value={formData.name || null}
              onChange={(_, value) => setFormData({ ...formData, name: value || '' })}
              renderInput={(params) => <TextField {...params} margin="normal" label="Term Name" required />}
            />
            <TextField
              fullWidth label="Year" type="number" value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: e.target.value })}
              required margin="normal"
            />
            <TextField
              fullWidth label="Start Date" type="date" value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              required margin="normal" InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth label="End Date" type="date" value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              required margin="normal" InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={<Checkbox checked={formData.is_current} onChange={(e) => setFormData({ ...formData, is_current: e.target.checked })} />}
              label="Set as current term"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm}>Cancel</Button>
            <Button type="submit" variant="contained">{editingTerm ? 'Update Term' : 'Save Term'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Term</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this term? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AcademicTerms;
